import { XLSX } from '../../utils/xlsxShim';

/* Parsing generique des fichiers « etat des reclamations » des rencontres
   bilaterales. Chaque pays a son gabarit : les feuilles portent le nom de la
   compagnie etrangere, et l'intitule de la colonne du numero sinistre BNCB
   Togo varie (SIN BNCB TG, N° SIN TOGO, NSIN BNCB TOGO, SINISTRE BNCB TOGO,
   TNB CLAIM NO., …). On detecte donc les colonnes par motif plutot que par nom
   exact, et on ne garde une feuille que si on y trouve la colonne du numero
   Togo - la cle du rapprochement cote backend. */

const norm = (v) => (v == null ? '' : String(v)
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .trim().toUpperCase().replace(/\s+/g, ' '));

/* Feuilles de synthese (recap, tableaux croises…) : elles dupliquent les
   lignes des feuilles compagnies - jamais parsees. */
const PREFIXES_FEUILLES_IGNOREES = [
  'RECAP', 'RECOURS', 'SITUATION', 'DONNEES', 'BASE', 'FEUIL', 'ETAT GLOBAL', 'REC ',
];

/* Feuille listant des bons a payer : chaque ligne vaut situation « BAP ». */
const estFeuilleBap = (nomNorme) => nomNorme.startsWith('BAP');

const estFeuilleIgnoree = (nomNorme) =>
  PREFIXES_FEUILLES_IGNOREES.some((p) => nomNorme.startsWith(p));

function toNumber(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  const brut = String(v).replace(/\s/g, '').replace(',', '.');
  // Refuse les cellules non purement numeriques (ex. « 2023/7/210 », « P.C »)
  // que parseFloat tronquerait en nombre plausible.
  if (!/^-?\d+(\.\d+)?$/.test(brut)) return null;
  const n = parseFloat(brut);
  return Number.isFinite(n) ? n : null;
}

function toIsoDate(v) {
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'number') {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  return null;
}

/* ── Reconnaissance des colonnes ────────────────────────────────────────── */

/** Colonne du numero sinistre BNCB Togo - jamais celle du bureau etranger
    (SIN BNCB CI/NE/NG/MALI…) ni celle de l'assureur (SIN ASSUREUR TOGO).
    Le libelle doit ressembler a une colonne de numero (SIN…, N°…, N …) pour
    ne pas confondre avec un simple libelle de groupe « BNCB TOGO ». */
const estColonneNumeroTogo = (cu) =>
  /TNB/.test(cu)
  || ((/SIN/.test(cu) || /^N[°O]? /.test(cu)) && !/ASSUREUR/.test(cu)
    && (/TOGO/.test(cu) || /\bTG\b/.test(cu) || /BNCB[ -]?T\b/.test(cu)));

/** Plusieurs colonnes BAP peuvent coexister (BAP, FG, BAP TOTAL) : on prend
    la plus complete. */
function scoreBap(cu) {
  if (cu === 'BAP TOTAL') return 4;
  if (cu.includes('BON A PAYER')) return 3;
  if (cu.includes('MONTANT') && cu.includes('BAP')) return 3;
  if (cu === 'BAP') return 2;
  if (cu === 'AGREED AMOUNT') return 1;
  return -1;
}

const estColonneDate = (cu) => /DATE/.test(cu) && /(SINISTRE|ACCIDENT)/.test(cu);
const estColonneSituation = (cu) => /SITUATION|ETAT DU DOSSIER|FILE STATUT/.test(cu);
const estColonneMontantReclame = (cu) =>
  cu === 'MONTANT' || cu === 'CLAIM AMOUNT' || (/MONTANT/.test(cu) && /RECLAM/.test(cu));

/** Lignes de tete qui ne sont PAS des fragments d'entete : bloc de stats
    (EVALUATION / NBRE DOSSIERS / % MT, avec un « BAP » trompeur) ou titre de
    feuille (phrase longue pouvant contenir « BON A PAYER »). */
const estLigneHorsEntete = (r) =>
  r.some((c) => {
    const cu = norm(c);
    return cu === 'EVALUATION' || cu === 'NBRE DOSSIERS' || cu.startsWith('% MT')
      || cu.length > 35;
  });

function chercherColonnes(r, cols, bapScoreActuel) {
  let bapScore = bapScoreActuel;
  r.forEach((c, j) => {
    const cu = norm(c);
    if (!cu) return;
    if (cols.tg == null && estColonneNumeroTogo(cu)) cols.tg = j;
    else if (cols.ds == null && estColonneDate(cu)) cols.ds = j;
    else if (cols.sit == null && estColonneSituation(cu)) cols.sit = j;
    else if (cols.mt == null && estColonneMontantReclame(cu)) cols.mt = j;
    else {
      const s = scoreBap(cu);
      if (s > bapScore) { bapScore = s; cols.bap = j; }
    }
  });
  return bapScore;
}

/** Repere la ligne d'entete (celle qui porte la colonne du numero Togo) et
    l'index des colonnes utiles. Les entetes sur plusieurs lignes (libelles
    fusionnes) sont completes depuis les 2 lignes precedentes. */
function reperColonnes(rows, maxHeader = 20) {
  let fallbackVDossier = null;
  for (let i = 0; i < Math.min(maxHeader, rows.length); i++) {
    const r = rows[i] || [];
    const cols = {};
    let bapScore = chercherColonnes(r, cols, -1);
    if (cols.tg == null) {
      if (fallbackVDossier == null) {
        const j = r.findIndex((c) => norm(c) === 'V/DOSSIER');
        if (j >= 0) fallbackVDossier = { header: i, cols: { ...cols, tg: j } };
      }
      continue;
    }
    // Entete multi-lignes : complete les colonnes manquantes juste au-dessus.
    for (let k = i - 1; k >= 0 && k >= i - 2; k--) {
      const prev = rows[k] || [];
      if (estLigneHorsEntete(prev)) break;
      bapScore = chercherColonnes(prev, cols, bapScore);
    }
    return { header: i, cols };
  }
  return fallbackVDossier;
}

/* ── Extraction ─────────────────────────────────────────────────────────── */

function parseFeuille(ws, nomFeuille, forceBap) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, cellDates: true });
  const rc = reperColonnes(rows);
  if (!rc) return [];
  const { header, cols } = rc;
  // Sur une feuille BAP sans colonne BAP explicite, la colonne MONTANT est le
  // montant du bon a payer.
  const idxBap = cols.bap != null ? cols.bap : (forceBap ? cols.mt : null);
  const out = [];
  for (let i = header + 1; i < rows.length; i++) {
    const r = rows[i] || [];
    const tg = cols.tg != null ? r[cols.tg] : null;
    if (!tg || !String(tg).trim()) continue;
    const tgn = norm(tg);
    // Lignes de pied : totaux, signatures…
    if (tgn.startsWith('TOTAL') || tgn.startsWith('FAIT A')) continue;
    const bap = idxBap != null ? toNumber(r[idxBap]) : null;
    if (forceBap && !bap) continue;
    out.push({
      sinBncbTg: String(tg).trim(),
      compagnie: forceBap ? 'BAP' : nomFeuille.trim(),
      dateSinistre: cols.ds != null ? toIsoDate(r[cols.ds]) : null,
      montantReclame: !forceBap && cols.mt != null ? toNumber(r[cols.mt]) : null,
      montantBap: bap,
      situation: forceBap ? 'BAP'
        : (cols.sit != null && r[cols.sit] ? String(r[cols.sit]).trim() : null),
    });
  }
  return out;
}

/**
 * Extrait toutes les lignes exploitables d'un classeur.
 * @returns {{ lignes: Array, feuillesLues: Array<{nom: string, nb: number}>,
 *             feuillesSansEntete: string[] }}
 */
export function extraireLignes(wb) {
  const lignes = [];
  const feuillesLues = [];
  const feuillesSansEntete = [];
  wb.SheetNames.forEach((nom) => {
    const nu = norm(nom);
    if (estFeuilleIgnoree(nu)) return;
    const extraites = parseFeuille(wb.Sheets[nom], nom, estFeuilleBap(nu));
    if (extraites.length) {
      lignes.push(...extraites);
      feuillesLues.push({ nom, nb: extraites.length });
    } else {
      feuillesSansEntete.push(nom);
    }
  });
  return { lignes, feuillesLues, feuillesSansEntete };
}
